import { useContext, useState, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { inject, observer, PropTypes as MobXPropTypes } from 'mobx-react';
import i18nContext from 'components/i18n-context';
import { filterPaymentAccounts } from 'components/common/PaymentForm/utils';
import { getAccountTitleWithFormatting } from 'components/utils';
import { isCryptoProvider, amountFormattedValue, isManualTypeProvider, isWallesterProvider } from 'services/utils';
import { CRYPTO_CURRENCY_PRECISION } from 'services/currenciesConstant';
import Button from 'uikit/Button/Button';
import { AccountCardSelector } from 'uikit/AccountCardSelector/AccountCardSelector';
import './SelectAccountStep.scss';
import { ACCOUNT_PROPERTIES } from '../../../../../common/constants';

const SelectAccountStep = ({ userAccounts, newPaymentStore }) => {
  const i18n = useContext(i18nContext);
  const [selectedAccount, setSelectedAccount] = useState(newPaymentStore.currentAccount || null);
  const [isSelectingAccount, setIsSelectingAccount] = useState(false);

  const paymentAccounts = useMemo(() => filterPaymentAccounts(userAccounts || []), [userAccounts]);

  const accountsData = useMemo(
    () =>
      paymentAccounts.map((account) => ({
        wallet_number: account[ACCOUNT_PROPERTIES.ACCOUNT_NUMBER],
        account: account,
        currency: account[ACCOUNT_PROPERTIES.CURRENCY],
        networkCode: account[ACCOUNT_PROPERTIES.NETWORK],
        balance: account[ACCOUNT_PROPERTIES.AVAILABLE] || 0,
        reserved: account[ACCOUNT_PROPERTIES.ON_HOLD] || 0,
        isCardAccount: isWallesterProvider(account),
        isManualAccount: isManualTypeProvider(account),
        isCryptoAccount: isCryptoProvider(account?.transfer_provider)
      })),
    [paymentAccounts]
  );

  const handleAccountSelect = (accountData) => {
    const account = paymentAccounts.find((acc) => acc.wallet_number === accountData.wallet_number);
    if (account) {
      setSelectedAccount(accountData);
      newPaymentStore.setAccountInfo(account);
      setIsSelectingAccount(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (selectedAccount) {
      newPaymentStore.goToNextStep();
    }
  };

  const handleAccountChange = useCallback(
    (name, walletNumber) => {
      const filteredSelectedAccount = paymentAccounts.find((account) => account.wallet_number === walletNumber);

      if (filteredSelectedAccount) {
        newPaymentStore.setAccountInfo(filteredSelectedAccount);
      }
    },
    [paymentAccounts, newPaymentStore]
  );

  useEffect(() => {
    if (newPaymentStore.currentAccount && paymentAccounts.length > 0) {
      const existingAccount = paymentAccounts.find(
        (acc) => acc.wallet_number === newPaymentStore.currentAccount.wallet_number
      );
      if (existingAccount) {
        const accountData = {
          wallet_number: existingAccount[ACCOUNT_PROPERTIES.ACCOUNT_NUMBER],
          account: existingAccount,
          currency: existingAccount[ACCOUNT_PROPERTIES.CURRENCY],
          networkCode: existingAccount[ACCOUNT_PROPERTIES.NETWORK],
          balance: existingAccount[ACCOUNT_PROPERTIES.AVAILABLE] || 0,
          reserved: existingAccount[ACCOUNT_PROPERTIES.ON_HOLD] || 0,
          isCardAccount: isWallesterProvider(existingAccount),
          isManualAccount: isManualTypeProvider(existingAccount),
          isCryptoAccount: isCryptoProvider(existingAccount?.transfer_provider)
        };
        setSelectedAccount(accountData);
      }
    }
  }, [newPaymentStore.currentAccount, paymentAccounts]);

  // Set initial account on mount
  useEffect(() => {
    if (paymentAccounts.length > 0 && !newPaymentStore.currentAccount) {
      handleAccountChange('', paymentAccounts[0].wallet_number);
    }
  }, [paymentAccounts, newPaymentStore.currentAccount, handleAccountChange]);

  return (
    <form onSubmit={handleSubmit} className={'select-account-step'}>
      <div className={'select-account-step-selector-wrapper'}>
        <AccountCardSelector
          type={'selector'}
          accounts={accountsData}
          selectedAccount={selectedAccount}
          onAccountSelect={handleAccountSelect}
          placeholder={i18n.getMessage('newPaymentForm.selectAccount.placeholder')}
          isSelectingAccount={isSelectingAccount}
          onToggleSelection={setIsSelectingAccount}
        />
      </div>
      {!isSelectingAccount && (
        <Button
          className={'select-account-step-next-button'}
          type={'primary'}
          roleType={'submit'}
          onClick={handleSubmit}
          isDisabled={!selectedAccount || newPaymentStore.isLoading}
        >
          {i18n.getMessage('templates.form.button.next')}
        </Button>
      )}
    </form>
  );
};

SelectAccountStep.propTypes = {
  userAccounts: PropTypes.array,
  newPaymentStore: MobXPropTypes.observableObject
};

export default inject((stores) => ({
  newPaymentStore: stores.newPaymentStore
}))(observer(SelectAccountStep));
